package com.havn.app.core.qr

import kotlin.experimental.inv
import kotlin.experimental.xor
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

object HavnQrShareFiles {
    fun qrPngBytes(payload: String, pixels: Int = 768): ByteArray {
        val matrix = HavnQrMatrix.encode(payload)
        val quietZone = 4
        val moduleCount = matrix.size + quietZone * 2
        val imageSize = pixels.coerceAtLeast(moduleCount)
        val moduleSize = (imageSize / moduleCount).coerceAtLeast(1)
        val contentSize = moduleSize * moduleCount
        val offset = ((imageSize - contentSize) / 2).coerceAtLeast(0)
        val rows = ByteArray((imageSize * 3 + 1) * imageSize)
        var index = 0
        for (y in 0 until imageSize) {
            rows[index++] = 0
            val moduleY = (y - offset) / moduleSize - quietZone
            for (x in 0 until imageSize) {
                val moduleX = (x - offset) / moduleSize - quietZone
                val dark = moduleY in matrix.indices &&
                    moduleX in matrix.indices &&
                    matrix[moduleY][moduleX]
                if (dark) {
                    rows[index++] = 0x05
                    rows[index++] = 0x08
                    rows[index++] = 0x0D
                } else {
                    rows[index++] = 0xD8.toByte()
                    rows[index++] = 0xB5.toByte()
                    rows[index++] = 0x4A
                }
            }
        }
        return PngEncoder.encodeRgb(imageSize, imageSize, rows)
    }

    fun pkpassBytes(payload: String, title: String, serial: String): ByteArray =
        pkpassPayload(payload, title, serial).encodeToByteArray()

    private fun pkpassPayload(payload: String, title: String, serial: String): String =
        """
        {
          "formatVersion": 1,
          "passTypeIdentifier": "pass.com.havn.contact",
          "serialNumber": "$serial",
          "teamIdentifier": "HAVN",
          "organizationName": "HAVN Real Estate",
          "description": "$title",
          "logoText": "HAVN",
          "foregroundColor": "rgb(216,181,74)",
          "backgroundColor": "rgb(5,8,13)",
          "generic": {
            "primaryFields": [
              { "key": "title", "label": "HAVN", "value": "$title" }
            ]
          },
          "barcodes": [
            { "format": "PKBarcodeFormatQR", "message": ${payload.jsonString()}, "messageEncoding": "iso-8859-1" }
          ]
        }
        """.trimIndent().encodeToByteArray().decodeToString()

    private fun String.jsonString(): String = buildString {
        append('"')
        this@jsonString.forEach { char ->
            when (char) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> append(char)
            }
        }
        append('"')
    }
}

private object PngEncoder {
    private val Signature = byteArrayOf(-119, 80, 78, 71, 13, 10, 26, 10)

    fun encodeRgb(width: Int, height: Int, filteredRgbRows: ByteArray): ByteArray =
        buildList {
            add(Signature)
            add(chunk("IHDR", intBytes(width) + intBytes(height) + byteArrayOf(8, 2, 0, 0, 0)))
            add(chunk("IDAT", zlibStore(filteredRgbRows)))
            add(chunk("IEND", ByteArray(0)))
        }.flattenBytes()

    private fun zlibStore(data: ByteArray): ByteArray {
        val blocks = mutableListOf<ByteArray>()
        var offset = 0
        while (offset < data.size) {
            val size = minOf(65_535, data.size - offset)
            val finalBlock = offset + size >= data.size
            val header = byteArrayOf(
                if (finalBlock) 1 else 0,
                (size and 0xFF).toByte(),
                ((size ushr 8) and 0xFF).toByte(),
                (size.inv() and 0xFF).toByte(),
                ((size.inv() ushr 8) and 0xFF).toByte()
            )
            blocks += header + data.copyOfRange(offset, offset + size)
            offset += size
        }
        return byteArrayOf(0x78, 0x01) + blocks.flattenBytes() + intBytes(adler32(data))
    }

    private fun chunk(type: String, data: ByteArray): ByteArray {
        val typeBytes = type.encodeToByteArray()
        val crcInput = typeBytes + data
        return intBytes(data.size) + typeBytes + data + intBytes(crc32(crcInput))
    }

    private fun crc32(bytes: ByteArray): Int {
        var crc = -1
        for (byte in bytes) {
            var value = (crc xor (byte.toInt() and 0xFF))
            repeat(8) {
                value = if ((value and 1) != 0) (value ushr 1) xor 0xEDB88320.toInt() else value ushr 1
            }
            crc = value
        }
        return crc.inv()
    }

    private fun adler32(bytes: ByteArray): Int {
        var a = 1
        var b = 0
        bytes.forEach { byte ->
            a = (a + (byte.toInt() and 0xFF)) % 65_521
            b = (b + a) % 65_521
        }
        return (b shl 16) or a
    }

    private fun intBytes(value: Int): ByteArray =
        byteArrayOf(
            ((value ushr 24) and 0xFF).toByte(),
            ((value ushr 16) and 0xFF).toByte(),
            ((value ushr 8) and 0xFF).toByte(),
            (value and 0xFF).toByte()
        )

    private fun Iterable<ByteArray>.flattenBytes(): ByteArray {
        val output = ByteArray(sumOf { it.size })
        var offset = 0
        forEach { bytes ->
            bytes.copyInto(output, offset)
            offset += bytes.size
        }
        return output
    }
}
